import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { AirspaceCheckerParametersWithStartEnd } from '@flyfreely-portal-ui/airspace';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LocationService,
    SiteArea,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    GEOMETRY_UPLOAD_ACCEPT,
    getFileName
} from '@flyfreely-portal-ui/locations';
import {
    FeatureGroup,
    MapDataInput,
    MapFeature
} from '@flyfreely-portal-ui/map';
import FileSaver from 'file-saver';
import {
    DefaultKmlFeatureTypesByGeometryType,
    KmlFeatureGroups,
    KmlUploadService,
    UploadedFeature,
    isSinglePolygon
} from 'libs/locations/src/lib/kml-upload.service';
import { showKmlUploadDialogue } from 'libs/locations/src/lib/kml-upload/kml-upload-dialogue.component';
import { LocationEditDrawingComponent } from 'libs/locations/src/lib/location-edit-drawing/location-edit-drawing.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

/** A single category ID used for all site areas (no type distinction). */
const SITE_AREA_CATEGORY_ID = 'SITE_AREA';

function siteAreasToFeatureGroups(
    siteAreas: SiteArea[],
    startId: number
): { features: FeatureGroup[]; nextId: number } {
    const existingFeatures: MapFeature[] = siteAreas.map((area, ix) => ({
        id: startId + ix,
        name: area.name,
        geom: area.boundary as GeoJSON.Polygon,
        categoryId: SITE_AREA_CATEGORY_ID
    }));

    return {
        nextId: startId + siteAreas.length,
        features: [
            {
                id: 0,
                name: 'Areas',
                type: 'Polygon',
                canAdd: true,
                minimumNumber: 0,
                maximumNumber: null,
                categories: [{ id: SITE_AREA_CATEGORY_ID, name: 'Site Area' }],
                existingFeatures,
                styles: {
                    fill: [
                        {
                            paint: {
                                'fill-color': '#0066cc',
                                'fill-opacity': 0.3
                            }
                        }
                    ],
                    line: [
                        {
                            paint: {
                                'line-color': '#0066cc',
                                'line-width': 2,
                                'line-opacity': 1
                            }
                        }
                    ],
                    symbol: [
                        {
                            layout: {
                                'text-field': '{name}',
                                'text-anchor': 'center',
                                'text-size': 12,
                                'text-font': [
                                    'Roboto Bold',
                                    'Arial Unicode MS Regular'
                                ]
                            },
                            paint: {
                                'text-halo-color': 'hsl(0, 100%, 100%)',
                                'text-halo-width': 1,
                                'text-halo-blur': 1
                            }
                        }
                    ]
                }
            }
        ]
    };
}

@Component({
    selector: 'site-location-edit',
    templateUrl: './site-location-edit.component.html'
})
export class SiteLocationEditComponent implements OnInit, OnDestroy {
    @Input() organisationId: number;
    @Input() siteAreas: SiteArea[] = [];
    @Input() siteName: string;

    @Output() siteAreasChange = new EventEmitter<SiteArea[]>();

    featureGroups: FeatureGroup[] = [];

    @ViewChild('drawingComponent')
    drawingComponent: LocationEditDrawingComponent;

    private nextId = 1;

    mapData$ = new BehaviorSubject<MapDataInput>({});
    airspaceValue$ = new BehaviorSubject<AirspaceCheckerParametersWithStartEnd>(
        {
            airspaceDate: null,
            airspaceDuration: 0,
            airspaceHeight: 0,
            airspaceRuleset: null,
            startTime: null,
            endTime: null,
            airspaceTimeZone: null
        }
    );

    workTracker = new WorkTracker();

    readonly GEOMETRY_UPLOAD_ACCEPT = GEOMETRY_UPLOAD_ACCEPT;

    isAddingFeature = false;
    hasAnyFeatures = false;
    private hasChanged = false;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef<SiteLocationEditComponent>,
        private commonDialoguesService: CommonDialoguesService,
        private locationService: LocationService,
        private kmlUploadService: KmlUploadService,
        private modalService: BsModalService,
        private logging: FlyFreelyLoggingService
    ) {}

    ngOnInit() {
        const built = siteAreasToFeatureGroups(this.siteAreas ?? [], 1);
        this.featureGroups = built.features;
        this.nextId = built.nextId + 1;
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.mapData$.complete();
        this.airspaceValue$.complete();
    }

    onFeatureGroupsChange(featureGroups: FeatureGroup[]) {
        this.featureGroups = featureGroups;
        this.hasChanged = true;
    }

    onIsAddingFeatureChange(value: boolean) {
        this.isAddingFeature = value;
    }

    onHasAnyFeaturesChange(value: boolean) {
        this.hasAnyFeatures = value;
    }

    private getSiteAreas(): SiteArea[] {
        return (
            this.drawingComponent?.getFeatureGroups() ?? this.featureGroups
        ).reduce(
            (acc, fg) =>
                acc.concat(
                    fg.existingFeatures.map(f => ({
                        name: f.name,
                        boundary: f.geom
                    }))
                ),
            [] as SiteArea[]
        );
    }

    private addSiteArea(name: string, geometry: GeoJSON.Geometry) {
        const newFeature: MapFeature = {
            id: this.nextId++,
            name,
            geom: geometry as GeoJSON.Polygon,
            categoryId: SITE_AREA_CATEGORY_ID
        };
        this.featureGroups = this.featureGroups.map(fg =>
            fg.id === 0
                ? {
                      ...fg,
                      existingFeatures: fg.existingFeatures.concat(newFeature)
                  }
                : fg
        );
        if (this.drawingComponent) {
            this.drawingComponent.internalFeatureGroups = this.featureGroups;
            this.drawingComponent.clearUndoHistory();
            this.drawingComponent.validateFeatures();
            this.drawingComponent.zoomToAllFeatures(false);
        }
    }

    // ---- KML / Upload ----

    uploadLocation(locationFiles: File) {
        const locationFile = locationFiles;

        if (
            locationFile.type === 'application/json' ||
            locationFile.name.toLowerCase().endsWith('.json')
        ) {
            this.logging.error(
                null,
                'The uploaded file was not of a valid KML/KMZ filetype.'
            );
            return;
        }

        this.locationService
            .uploadLocation(locationFile)
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                take(1),
                tap({
                    next: DO_NOTHING,
                    error: (error: FlyFreelyError) =>
                        this.logging.error(
                            error,
                            `Error uploading KML file: ${error.message}`
                        )
                })
            )
            .subscribe(featureCollection => {
                const defaultFeatureTypes: DefaultKmlFeatureTypesByGeometryType =
                    {
                        polygon: SITE_AREA_CATEGORY_ID as any,
                        point: SITE_AREA_CATEGORY_ID as any,
                        lineString: SITE_AREA_CATEGORY_ID as any
                    };

                const features = this.kmlUploadService
                    .parseKmlGroupings(featureCollection, defaultFeatureTypes)
                    .filter(feature => feature.featureType === 'Areas');

                if (features.length === 0) {
                    this.logging.error(
                        null,
                        `The uploaded KML does not contain any valid polygon geometry for creating a site area.`
                    );
                } else if (isSinglePolygon(features)) {
                    this.parseSingleKmlFeature(
                        features[0].features[0],
                        locationFile.name
                    );
                } else {
                    showKmlUploadDialogue(
                        this.modalService,
                        features,
                        locationFile.name,
                        [SITE_AREA_CATEGORY_ID as any]
                    )
                        .content.selectedFeatures.pipe(
                            takeUntil(this.ngUnsubscribe$)
                        )
                        .subscribe(features =>
                            this.parseComplexKml(features, locationFile.name)
                        );
                }
            })
            .add(this.workTracker.createTracker());
    }

    parseSingleKmlFeature(feature: UploadedFeature, fileName: string) {
        this.addSiteArea(fileName, feature.geometry);
    }

    parseComplexKml(features: KmlFeatureGroups[], name: string) {
        features.forEach(group => {
            group.features.forEach(f => {
                this.addSiteArea(f.name, f.geometry);
            });
        });
    }

    downloadKml() {
        const name = this.siteName ?? 'Site';
        const locationFeatures = this.getSiteAreas().map(a => ({
            name: a.name,
            geometry: a.boundary,
            type: SITE_AREA_CATEGORY_ID as any
        }));
        this.locationService
            .downloadKmlFromFeatures({ name, features: locationFeatures })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: download => {
                    const headers = download.headers;
                    const fileName =
                        getFileName(headers.get('content-disposition')) ??
                        'flyfreely.kml';

                    FileSaver.saveAs(download.body, fileName);
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error downloading KML file: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    save() {
        this.siteAreasChange.emit(this.getSiteAreas());
        this.hasChanged = false;
        this.modal.hide();
    }

    close() {
        if (this.hasChanged) {
            this.commonDialoguesService
                .showConfirmationDialogue(
                    'Confirm Cancel',
                    'You have unsaved changes, are you sure you want to cancel?',
                    'Yes',
                    () => Promise.resolve()
                )
                .then(() => this.modal.hide())
                .catch(() => undefined);
        } else {
            this.modal.hide();
        }
    }
}
